import { useAccountStatuses } from '../accountStatus'
import { type GetHookResponse } from '../hooks'
import { dispositionsKey } from '../keys'
import { useTantalimQuery } from '../query'
import { useDispositionCache } from './cache'
import {
  type DispositionData,
  type DispositionId,
  type DispositionListInput,
  type DispositionListSuccess,
  HOUR,
  isDefined,
  isEmpty,
  isNotEmpty,
  MINUTE,
  sortAccountStatuses,
} from '@terros/common'
import { useMemo } from 'react'

export function useDispositions(input?: DispositionListInput): GetHookResponse<DispositionData[]> {
  const dispositionCache = useDispositionCache()
  const state = useTantalimQuery<DispositionListInput, DispositionListSuccess, DispositionData[]>({
    url: '/disposition/list',
    queryKey: dispositionsKey(input?.parentOnly ?? false, input),
    input: input ?? {},
    convertResponse: (data) => {
      data.dispositions.forEach(dispositionCache.addItem)
      return data.dispositions
    },
    options: {
      staleTime: 30 * MINUTE,
      gcTime: HOUR,
      retry: false,
    },
  })
  return { ...state }
}

export const useDispositionsMap = (input?: DispositionListInput): Map<DispositionId, DispositionData> => {
  const { data } = useDispositions(input)
  return useMemo(() => toDispositionMap(data) ?? new Map(), [data])
}

export const toDispositionMap = (
  dispositions: DispositionData[] | undefined
): Map<DispositionId, DispositionData> | undefined => {
  if (isEmpty(dispositions)) return
  return new Map<DispositionId, DispositionData>(
    dispositions.map((disposition) => [disposition.dispositionId, disposition])
  )
}

export const useLeadDisposition = (): DispositionData | undefined => {
  const { data } = useAccountStatuses()
  const { data: dispositions } = useDispositions()
  const linkedStatusIds = useMemo(() => dispositions?.map((d) => d.accountStatusId).filter(isDefined), [dispositions])

  return useMemo(() => {
    if (!data || !linkedStatusIds) return
    const statuses = data.statuses.filter(
      (status) => isNotEmpty(status.order) && linkedStatusIds.includes(status.statusId)
    )

    const leadStatus = sortAccountStatuses(statuses).at(0)
    if (!leadStatus) return undefined
    return dispositions?.find((d) => d.accountStatusId === leadStatus.statusId)
  }, [linkedStatusIds, data, dispositions])
}
